<script setup lang="ts">
import type { Diagnostic } from "@/types";
import type { Finding } from "@/types/findings.types";
import { computed, ref, toRef } from "vue";
import { useTranslation } from "i18next-vue";
import {
  useGetReportSectionsTree,
  useCreateFinding,
  useGetUnsortedFindings,
} from "@/stores/queries";
import { HEADER_HEIGHT, TABS_HEIGHT } from "../../layout/constants";
import ReportState from "@/stores/report/ReportState";
import SectionFindingsTable from "./SectionFindingsTable.vue";
import UnsortedFindingsTable from "./UnsortedFindingsTable.vue";
import UpdateFindingDialog from "./finding-dialog/UpdateFindingDialog.vue";
import SectionActionsButton from "../../sections/SectionActionsButton.vue";
import type { SectionItem } from "@/types/sections.types";
import FindingsBadge from "./FindingsBadge.vue";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import SettingsSortedListInput from "@/components/report-templates/settings/SettingsSortedListInput.vue";
import {
  useFindingsTableColumns,
  type FindingsColumnKey,
} from "@/composable/useFindingsTableColumns";

const props = defineProps<{
  diagnostic: Diagnostic;
  reportId: number;
}>();

const { t } = useTranslation();
const diagnosticId = computed(() => props.diagnostic.id);
const reportIdRef = toRef(props, "reportId");
const topOffset = HEADER_HEIGHT + TABS_HEIGHT;

const { data: sectionsTree } = useGetReportSectionsTree(reportIdRef);
const { data: unsortedFindings } = useGetUnsortedFindings(reportIdRef);
const { mutateAsync: createFinding } = useCreateFinding();

// Unsorted section state
const unsortedFindingsCount = ref(0);

const hasUnsortedFindings = computed(
  () => (unsortedFindings.value?.length ?? 0) > 0,
);

// Flatten sections with parent tracking for collapse logic
type SectionGroup = {
  id: string;
  sectionId: number;
  title: string;
  path?: string;
  parentIds: string[];
  cataloguePartId?: number;
  hasChildren: boolean;
  section: SectionItem;
};

const flattenSectionsTree = computed(() => {
  if (!sectionsTree.value) return [];

  const result: SectionGroup[] = [];

  const flatten = (sections: SectionItem[], parentIds: string[] = []): void => {
    for (const section of sections) {
      const currentId = `section-${section.id}`;
      const hasChildren = !!(section.children && section.children.length > 0);
      result.push({
        id: currentId,
        sectionId: section.id,
        title: section.title,
        path: section.path,
        parentIds,
        hasChildren,
        section,
      });

      if (section.children?.length) {
        flatten(section.children, [...parentIds, currentId]);
      }
    }
  };

  flatten(sectionsTree.value);
  return result;
});

// Collapsed state (persisted in ReportState)
const toggleGroup = (groupId: string) => {
  ReportState.toggleFindingsGroup(groupId);
};

const isGroupCollapsed = (groupId: string) =>
  ReportState.isFindingsGroupCollapsed(groupId);

const isGroupHidden = (group: SectionGroup): boolean => {
  for (const parentId of group.parentIds) {
    if (ReportState.isFindingsGroupCollapsed(parentId)) {
      return true;
    }
  }
  return false;
};

// Findings count tracking per section
const findingsCounts = ref<Map<number, number>>(new Map());

const updateFindingsCount = (sectionId: number, count: number) => {
  findingsCounts.value.set(sectionId, count);
};

const getFindingsCount = (sectionId: number) => {
  return findingsCounts.value.get(sectionId) ?? 0;
};

// Edit dialog state
const editingFinding = ref<Finding | null>(null);
const editingSectionId = ref<number | null>(null);
const showEditDialog = ref(false);

const openEditDialog = (finding: Finding, sectionId: number | null = null) => {
  editingFinding.value = finding;
  editingSectionId.value = sectionId;
  showEditDialog.value = true;
};

// Create finding in a section
const handleCreateFinding = async (sectionId: number) => {
  await createFinding({ reportId: props.reportId, sectionId });
};

// Create unsorted finding (no section)
const handleCreateUnsortedFinding = async () => {
  await createFinding({ reportId: props.reportId, sectionId: null });
};

// Column settings
const { visibleColumns, setVisibleColumns, configurableColumns } =
  useFindingsTableColumns();

const columnOptions = computed(() =>
  configurableColumns.map((col) => ({
    label: t(`report:findings.columns.${col}`),
    value: col,
  })),
);

const showColumnsModal = ref(false);
const pendingColumns = ref<FindingsColumnKey[]>([]);

const openColumnsModal = () => {
  pendingColumns.value = [...visibleColumns.value];
  showColumnsModal.value = true;
};

const confirmColumnsChange = () => {
  setVisibleColumns(pendingColumns.value);
  showColumnsModal.value = false;
};
</script>

<template>
  <div
    class="findings-step d-flex flex-column flex-grow-1"
    :style="`margin-top: ${topOffset}px`"
  >
    <!-- Scrollable content -->
    <el-scrollbar class="flex-grow-1">
      <div class="p-4">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h5 class="mb-0">{{ t("report:navigation.findings") }}</h5>
          <DRBtnIcon
            icon="bi-layout-three-columns"
            variant="outline"
            :size="32"
            @click="openColumnsModal"
          />
        </div>

        <!-- Unsorted findings section (À trier) -->
        <div v-if="hasUnsortedFindings" class="unsorted-section mb-4">
          <!-- Section header -->
          <div
            class="unsorted-header d-flex align-items-center gap-3 py-4 px-3"
          >
            <el-icon
              size="16"
              class="cursor-pointer"
              @click="
                ReportState.findingsUnsortedCollapsed =
                  !ReportState.findingsUnsortedCollapsed
              "
            >
              <i
                :class="
                  ReportState.findingsUnsortedCollapsed
                    ? 'bi-chevron-right'
                    : 'bi-chevron-down'
                "
              ></i>
            </el-icon>
            <div class="flex-grow-1 d-flex align-items-center gap-2">
              <span
                class="me-2 bg-warning text-white rounded-circle shadow"
                style="
                  aspect-ratio: 1;
                  width: px;
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                "
              >
                <el-icon size="1.5rem">
                  <i class="bi-exclamation text-dark"></i>
                </el-icon>
              </span>
              <span class="fw-medium">
                {{ t("report:findings.unsorted.title") }}
              </span>
              <span class="badge bg-dark text-white ms-4">
                {{ unsortedFindings?.length ?? 0 }}
                {{
                  $t("report:navigation.finding", {
                    count: unsortedFindings?.length ?? 0,
                  })
                }}
              </span>
            </div>

            <!-- Add finding button -->
            <!-- <DRBtn
              class="ms-auto"
              variant="outline"
              size="sm"
              icon="bi-plus-lg"
              :label="t('report:findings.addFinding')"
              @click.stop="handleCreateUnsortedFinding"
            /> -->
          </div>

          <!-- Unsorted findings table -->
          <UnsortedFindingsTable
            v-if="!ReportState.findingsUnsortedCollapsed"
            class="m-4"
            :diagnostic-id="diagnosticId"
            :report-id="reportId"
            @edit="(finding) => openEditDialog(finding, null)"
            @count="(count) => (unsortedFindingsCount = count)"
            @create="handleCreateUnsortedFinding"
          />
        </div>

        <!-- Empty state -->
        <div
          v-if="!flattenSectionsTree.length && !hasUnsortedFindings"
          class="empty-state d-flex flex-column align-items-center justify-content-center py-5 text-muted"
        >
          <el-icon size="64" class="mb-3"><i class="bi-diagram-3"></i></el-icon>
          <p>{{ $t("report:overviews.sections.empty") }}</p>
        </div>

        <!-- Groups -->
        <div v-if="flattenSectionsTree.length" class="groups-container">
          <div
            v-for="section in flattenSectionsTree"
            v-show="!isGroupHidden(section)"
            :key="section.id"
            class="group-section mb-3"
            :style="{ marginLeft: `${section.parentIds.length * 16}px` }"
          >
            <!-- Group header -->
            <div class="group-header d-flex align-items-center gap-3 py-4 px-3">
              <el-icon
                size="16"
                class="text-muted cursor-pointer"
                @click="toggleGroup(section.id)"
              >
                <i
                  :class="
                    isGroupCollapsed(section.id)
                      ? 'bi-chevron-right'
                      : 'bi-chevron-down'
                  "
                ></i>
              </el-icon>
              <div class="flex-grow-1">
                <span class="fw-medium">
                  {{ section.path }}. {{ section.title }}
                </span>
                <FindingsBadge :count="getFindingsCount(section.sectionId)" />
              </div>

              <!-- Section actions button -->
              <SectionActionsButton
                class="ms-auto"
                :section="section.section"
                :report-id="reportId"
                :size="32"
                findings-actions
                @create-finding="handleCreateFinding(section.sectionId)"
                @click.stop
              />
            </div>

            <!-- Group content (findings table) -->
            <SectionFindingsTable
              class="ms-2 mt-1"
              v-if="!isGroupCollapsed(section.id)"
              :section-id="section.sectionId"
              :catalogue-part-id="section.cataloguePartId"
              :diagnostic-id="diagnosticId"
              :report-id="reportId"
              :has-children="section.hasChildren"
              @edit="(finding) => openEditDialog(finding, section.sectionId)"
              @count="(count) => updateFindingsCount(section.sectionId, count)"
              @create="handleCreateFinding(section.sectionId)"
            />
          </div>
        </div>
      </div>
    </el-scrollbar>
  </div>

  <!-- Columns settings modal -->
  <el-dialog
    v-model="showColumnsModal"
    :title="t('report:findings.columns.title')"
    width="360px"
    append-to-body
  >
    <SettingsSortedListInput
      :model-value="pendingColumns"
      :options="columnOptions"
      @update:model-value="
        (val: string[]) => (pendingColumns = val as FindingsColumnKey[])
      "
    />
    <template #footer>
      <div class="d-flex justify-content-end gap-2">
        <button
          class="btn btn-outline-secondary"
          @click="showColumnsModal = false"
        >
          {{ t("cancel") }}
        </button>
        <button class="btn btn-primary" @click="confirmColumnsChange">
          {{ t("save") }}
        </button>
      </div>
    </template>
  </el-dialog>

  <!-- Edit finding dialog -->
  <UpdateFindingDialog
    v-if="editingFinding"
    v-model="showEditDialog"
    :finding="editingFinding"
    :section-id="editingSectionId"
    :diagnostic-id="diagnostic.id"
    :report-id="reportId"
    @close="
      editingFinding = null;
      editingSectionId = null;
    "
  />
</template>

<style lang="scss" scoped>
.findings-step {
  height: calc(100vh - v-bind("topOffset + 'px'"));
  width: 100%;
  background-color: var(--bs-light);
}

.group-header {
  background-color: transparent;
  transition: background-color 0.15s;
  font-size: 0.9rem;

  &:hover {
    background-color: var(--bs-gray-200);
  }
}

.unsorted-section {
  border: 2px dashed var(--bs-warning);
  border-radius: 8px;
  background-color: rgba(var(--bs-warning-rgb), 0.05);
}

.unsorted-header {
  background-color: transparent;
  transition: background-color 0.15s;
  font-size: 0.9rem;

  &:hover {
    background-color: rgba(var(--bs-warning-rgb), 0.1);
  }
}

.cursor-pointer {
  cursor: pointer;
}

.empty-state {
  min-height: 400px;
}

:deep(.finding-thumbnail) {
  width: 32px;
  height: 32px;
  object-fit: cover;
  border-radius: 4px;
}
</style>
